import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatSnackBar } from '@angular/material';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Handle } from '../../handle';
import { GatewayService } from 'src/app/gateway.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { LocalSaveService } from 'src/app/services/local-save-service.service';

// librerías para Excel y guardar
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';

@Component({
  selector: 'app-filtros-repcampana',
  templateUrl: './filtros-repcampana.component.html',
  styleUrls: ['./filtros-repcampana.component.scss']
})
export class FiltrosRepcampanaComponent implements OnInit {
  grupoVal = new FormGroup({
    fechaInicio: new FormControl('', Validators.required),
    fechaFin: new FormControl('', Validators.required),
  });

  // configuración de lotes
  cantPorPagina: number = 1000; // registros por lote (ajustable)
  cantTotalRegistros: number = 0;

  // progreso y estado
  descargando: boolean = false;
  progreso: number = 0; // porcentaje 0-100
  registrosObtenidos: number = 0;
  loteActual: number = 0;
  totalLotes: number = 0;
  reintentosPorLote: number = 3;
  mensajeEstado: string = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private localSave: LocalSaveService,
    private dialogRef: MatDialogRef<FiltrosRepcampanaComponent>,
    public Handle: Handle,
    private spinner: NgxSpinnerService,
    private snackbar: MatSnackBar,
    private gateway: GatewayService
  ) { }

  ngOnInit() {}

  getError(type) {
    if (type === 1) {
      if (this.grupoVal.controls.fechaInicio.hasError('required')) {
        return 'Debes seleccionar una fecha de inicio.';
      }
    } else if (type === 2) {
      if (this.grupoVal.controls.fechaFin.hasError('required')) {
        return 'Debes seleccionar una fecha fin.';
      }
    }
  }

  onNoClick() {
    this.dialogRef.close({ valid: false });
  }

  async descargar() {
    // Reiniciar estados
    this.cantTotalRegistros = 0;
    this.progreso = 0;
    this.registrosObtenidos = 0;
    this.loteActual = 0;
    this.mensajeEstado = '';
    this.descargando = true;

    const params = {
      FECHA_INICIO: this.grupoVal.controls.fechaInicio.value,
      FECHA_FIN: this.grupoVal.controls.fechaFin.value,
    };

    try {
      // 1) obtener cantidad total (endpoint interno)
      const result: any = await this.gateway.get('getCantRegistros', params);
      const aux: any = result;
      this.cantTotalRegistros = Number(aux.cantidad) || 0;

      if (!this.cantTotalRegistros) {
        this.snackbar.open('No se encontraron registros para descargar con el rango de fechas seleccionado.', 'Cerrar', { duration: 5000 });
        this.descargando = false;
        return;
      }

      // 2) calcular total de lotes
      this.totalLotes = Math.ceil(this.cantTotalRegistros / this.cantPorPagina);

      // 3) recorrer lotes secuencialmente y acumular resultados
      const todasFilas: any[] = [];

      for (let i = 0; i < this.totalLotes; i++) {
        this.loteActual = i;
        this.mensajeEstado = `Descargando lote ${i + 1} de ${this.totalLotes}...`;
        const paginaOffset = i * this.cantPorPagina;

        // intentar obtener el lote con reintentos
        const filasLote = await this.obtenerLoteConReintentos(paginaOffset, this.cantPorPagina, this.reintentosPorLote);

        // si el lote falla y devolvemos [] después de reintentos, podemos continuar (o decidir abortar).
        // Aquí seguimos para tratar de recopilar lo más posible.
        if (filasLote && filasLote.length) {
          // si tu Gateway devuelve objetos con propiedades anidadas, adapta esto según estructura
          filasLote.forEach(r => todasFilas.push(r));
          this.registrosObtenidos += filasLote.length;
        }

        // actualizar progreso
        this.progreso = Math.min(100, (this.registrosObtenidos / this.cantTotalRegistros) * 100);
      }

      // 4) final: generar Excel si hay datos
      if (todasFilas.length) {
        this.mensajeEstado = 'Generando archivo Excel...';
        await this.generarYDescargarExcel(todasFilas);
        this.snackbar.open('Descarga completada.', 'Cerrar', { duration: 5000 });
        this.dialogRef.close({ valid: true, registros: todasFilas.length });
      } else {
        this.snackbar.open('No se pudieron obtener registros para crear el archivo.', 'Cerrar', { duration: 5000 });
        this.dialogRef.close({ valid: false });
      }

    } catch (err) {
      this.snackbar.open('Error al iniciar la descarga. Intenta de nuevo más tarde.', 'Cerrar', { duration: 5000 });
    } finally {
      this.descargando = false;
      this.mensajeEstado = '';
      this.progreso = 100;
    }
  }

  /**
   * Obtiene un lote (offset basado) con reintentos.
   * paginaOffset = index * cantPorPagina
   */
  obtenerLoteConReintentos(paginaOffset: number, cantidad: number, maxReintentos: number): Promise<any[]> {
    return new Promise(async (resolve) => {
      let intento = 0;

      const paramsBase = {
        FECHA_INICIO: this.grupoVal.controls.fechaInicio.value,
        FECHA_FIN: this.grupoVal.controls.fechaFin.value,
        CANT_POR_PAGINA: cantidad,
        PAGINA: paginaOffset
      };

      const intentar = async () => {
        intento++;
        this.mensajeEstado = `Descargando lote ${this.loteActual + 1} (intento ${intento}/${maxReintentos})...`;
        try {
          const res: any = await this.gateway.get('getRegistros', paramsBase);
          const aux: any = res;
          const registros = aux.registros || [];
          return resolve(registros);
        } catch (e) {
          if (intento < maxReintentos) {
            // pequeño delay antes de reintento (ej. 1s)
            setTimeout(intentar, 1000);
          } else {
            // después de max reintentos devolver array vacío (se registra el fallo)
            this.snackbar.open(`Fallo el lote ${Math.floor(paginaOffset / this.cantPorPagina) + 1} después de ${maxReintentos} intentos. Continuando...`, 'Cerrar', { duration: 6000 });
            return resolve([]);
          }
        }
      };

      intentar();
    });
  }

  /**
   * Genera un workbook XLSX y dispara la descarga en navegador.
   * Usa SheetJS (xlsx) y FileSaver.
   */
  async generarYDescargarExcel(filas: any[]) {
    // Normalizar: convertir objetos a array de objetos planos para SheetJS
    // Si quieres un orden específico de columnas, define headers aquí:
    const headers = this.extraerHeaders(filas);
    // convertir filas (si quieres, puedes mapear/transformar valores)
    const worksheet = XLSX.utils.json_to_sheet(filas, { header: headers });
    const workbook = { Sheets: { 'Reporte': worksheet }, SheetNames: ['Reporte'] };
    const wbout: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    const fileName = this.generarNombreArchivo();
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    FileSaver.saveAs(blob, fileName);
  }

  generarNombreArchivo(): string {
    const fi = this.formatoFechaParaNombre(this.grupoVal.controls.fechaInicio.value);
    const ff = this.formatoFechaParaNombre(this.grupoVal.controls.fechaFin.value);
    const ahora = new Date();
    const ts = ahora.getFullYear() + ('0' + (ahora.getMonth()+1)).slice(-2) + ('0' + ahora.getDate()).slice(-2) + '_' + ('0' + ahora.getHours()).slice(-2) + ('0' + ahora.getMinutes()).slice(-2);
    return `reporte_${fi}_a_${ff}_${ts}.xlsx`;
  }

  formatoFechaParaNombre(inputDate: any): string {
    if (!inputDate) return 'sinfecha';
    const d = new Date(inputDate);
    const year = d.getFullYear();
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const day = ('0' + d.getDate()).slice(-2);
    return `${year}${month}${day}`;
  }

  /**
   * Extrae las keys (headers) a partir del primer objeto.
   * Esto asegura orden consistente de columnas.
   */
  extraerHeaders(filas: any[]): string[] {
    if (!filas || filas.length === 0) return [];
    const first = filas[0];
    // mantener el orden de las propiedades tal cual vengan
    return Object.keys(first);
  }
}
